import type { AssetRef } from '@flashpoint/site-dna';

export function AssetSlot({
  label,
  hint,
  asset,
  busy,
  ready,
  onPick,
  onClear,
}: {
  label: string;
  hint?: string;
  asset?: AssetRef;
  busy?: boolean;
  ready?: boolean;
  onPick: (file: File) => void;
  onClear?: () => void;
}) {
  return (
    <div className={`fp-row ${ready ? 'fp-row-owned' : ''}`}>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <span className="fp-label">{label}</span>
        {hint ? <span className="text-[11px] text-nm-tertiary">{hint}</span> : null}
      </div>
      {asset ? (
        <div className="mb-2 flex items-center gap-3">
          <img
            src={asset.downloadURL}
            alt={asset.alt ?? ''}
            className="h-14 w-14 rounded-[8px] border border-nm-border object-cover"
          />
          {onClear ? (
            <button type="button" onClick={onClear} className="text-xs text-nm-danger hover:underline">
              Remove
            </button>
          ) : null}
        </div>
      ) : null}
      <FilePicker busy={busy} onPick={onPick} />
    </div>
  );
}

export function GallerySlot({
  items,
  busy,
  onPick,
  onRemove,
}: {
  items: AssetRef[];
  busy?: boolean;
  onPick: (file: File) => void;
  onRemove: (index: number) => void;
}) {
  const ready = items.length > 0;
  return (
    <div className={`fp-row ${ready ? 'fp-row-owned' : ''}`}>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <span className="fp-label">Project / extra shots</span>
        <span className="text-[11px] text-nm-tertiary">Work page + before/after</span>
      </div>
      {items.length > 0 ? (
        <div className="mb-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
          {items.map((ref, i) => (
            <div key={`${ref.storagePath}-${i}`} className="relative">
              <img
                src={ref.downloadURL}
                alt={ref.alt ?? ''}
                className="aspect-square w-full rounded-[8px] border border-nm-border object-cover"
              />
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="absolute right-1 top-1 rounded bg-nm-bg/80 px-1 text-[10px] text-nm-danger hover:opacity-80"
              >
                x
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <FilePicker busy={busy} onPick={onPick} />
    </div>
  );
}

function FilePicker({
  busy,
  onPick,
}: {
  busy?: boolean;
  onPick: (file: File) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-center rounded-[10px] border border-dashed border-nm-border px-4 py-3 text-xs text-nm-muted hover:border-nm-accent hover:text-nm-ink">
      <input
        type="file"
        accept="image/*"
        className="hidden"
        disabled={busy}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.currentTarget.value = '';
        }}
      />
      {busy ? 'Uploading…' : 'Drop or tap to upload'}
    </label>
  );
}
